import React, { useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import AsyncSelect from 'react-select/async';
import { Search, Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import StockService from '../../Api_Module/Api_Services/StockService';
import { SubmissionFormValues, TickerOption } from '../../interface/HallOfFame';
import { useTheme } from '../ThemeContext';

interface SubmissionFormProps {
  onSubmit: (values: SubmissionFormValues) => Promise<void>;
  isSubmitting: boolean;
}

export const SubmissionForm: React.FC<SubmissionFormProps> = ({ onSubmit, isSubmitting }) => {
  const { theme } = useTheme();
  const [selectedTickers, setSelectedTickers] = useState<{
    [key: string]: TickerOption | null;
  }>({
    ticker1: null,
    ticker2: null,
    ticker3: null,
    ticker4: null,
    ticker5: null,
  });
  const [showLockInWarning, setShowLockInWarning] = useState(false);

  // Validation schema using Yup
  const validationSchema = Yup.object({
    name: Yup.string()
      .min(2, 'Name must be at least 2 characters')
      .max(100, 'Name is too long')
      .required('Full name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    socialHandle: Yup.string()
      .min(2, 'Social handle must be at least 2 characters')
      .max(100, 'Handle is too long')
      .optional(),
    experienceLevel: Yup.string()
      .oneOf(['beginner', 'intermediate', 'advanced', 'professional'])
      .optional(),
    ticker1: Yup.string().required('Stock 1 is required'),
    ticker2: Yup.string()
      .required('Stock 2 is required')
      .test('unique', 'Duplicate ticker', function (value) {
        const { ticker1 } = this.parent;
        return value !== ticker1;
      }),
    ticker3: Yup.string()
      .required('Stock 3 is required')
      .test('unique', 'Duplicate ticker', function (value) {
        const { ticker1, ticker2 } = this.parent;
        return value !== ticker1 && value !== ticker2;
      }),
    ticker4: Yup.string()
      .required('Stock 4 is required')
      .test('unique', 'Duplicate ticker', function (value) {
        const { ticker1, ticker2, ticker3 } = this.parent;
        return value !== ticker1 && value !== ticker2 && value !== ticker3;
      }),
    ticker5: Yup.string()
      .required('Stock 5 is required')
      .test('unique', 'Duplicate ticker', function (value) {
        const { ticker1, ticker2, ticker3, ticker4 } = this.parent;
        return value !== ticker1 && value !== ticker2 && value !== ticker3 && value !== ticker4;
      }),
    acceptTerms: Yup.boolean()
      .oneOf([true], 'You must accept the accountability lock')
      .required('You must accept the accountability lock'),
  });

  // Load ticker options from API
  const loadTickerOptions = async (inputValue: string): Promise<TickerOption[]> => {
    if (!inputValue || inputValue.length < 1) return [];

    try {
      const results = await StockService.getStockSymbolsSearch(inputValue);
      if (!results || results.length === 0) return [];

      return results.slice(0, 20).map((item: any) => ({
        value: item.symbol.toUpperCase(),
        label: `${item.symbol} - ${item.name || item.companyName || ''}`,
        name: item.name || item.companyName || '',
        price: item.price,
        logoUrl: item.logo,
      }));
    } catch (error) {
      console.error('Error loading tickers:', error);
      return [];
    }
  };

  // Check if ticker is already selected
  const isTickerSelected = (ticker: string, currentField: string): boolean => {
    return Object.entries(selectedTickers).some(
      ([key, value]) => key !== currentField && value?.value === ticker
    );
  };

  const handleTickerSelect = (
    option: TickerOption | null,
    field: string,
    setFieldValue: (field: string, value: any) => void
  ) => {
    if (option && isTickerSelected(option.value, field)) {
      toast.error(`${option.value} is already selected!`);
      return;
    }

    setSelectedTickers((prev) => ({ ...prev, [field]: option }));
    setFieldValue(field, option?.value || '');

    // Also set the current price for this ticker
    const priceField = field.replace('ticker', 'price');
    setFieldValue(priceField, option?.price || undefined);

    console.log('🎯 Selected ticker:', field, option?.value, 'at price:', option?.price);
  };

  const handleFormSubmit = async (values: SubmissionFormValues) => {
    if (!showLockInWarning) {
      setShowLockInWarning(true);
      toast.warning('⚠️ Warning: Once submitted, these 5 picks are permanent for the year!', {
        autoClose: 5000,
      });
      return;
    }

    await onSubmit(values);
  };

  const customSelectStyles = {
    control: (base: any) => ({
      ...base,
      backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
      borderColor: theme === 'dark' ? '#374151' : '#d1d5db',
      color: theme === 'dark' ? '#f9fafb' : '#111827',
    }),
    menu: (base: any) => ({
      ...base,
      backgroundColor: theme === 'dark' ? '#1f2937' : '#ffffff',
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isFocused
        ? theme === 'dark'
          ? '#374151'
          : '#f3f4f6'
        : 'transparent',
      color: theme === 'dark' ? '#f9fafb' : '#111827',
    }),
    singleValue: (base: any) => ({
      ...base,
      color: theme === 'dark' ? '#f9fafb' : '#111827',
    }),
    input: (base: any) => ({
      ...base,
      color: theme === 'dark' ? '#f9fafb' : '#111827',
    }),
  };

  return (
    <div className="max-w-4xl mx-auto bg-theme-secondary rounded-lg shadow-xl p-8">
      <div className="mb-8 text-center">
        <h2 className="text-3xl font-bold text-theme-primary mb-2">Enter The Hall of Fame</h2>
        <p className="text-theme-muted mb-1">
          Pick your 5 best stock ideas for the year. No changes allowed once locked in! 🔒
        </p>
        <p className="text-sm text-theme-muted">
          * Required fields | Other fields are optional but help us build a better community
        </p>
      </div>

      <Formik
        initialValues={{
          name: '',
          email: '',
          socialHandle: '',
          experienceLevel: undefined,
          ticker1: '',
          ticker2: '',
          ticker3: '',
          ticker4: '',
          ticker5: '',
          price1: undefined,
          price2: undefined,
          price3: undefined,
          price4: undefined,
          price5: undefined,
          acceptTerms: false,
        }}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
      >
        {({ values, setFieldValue, errors, touched }) => (
          <Form className="space-y-6">
            {/* Personal Information Section */}
            <div className="bg-theme-tertiary/30 p-6 rounded-lg space-y-4">
              <h3 className="text-xl font-semibold text-theme-primary mb-4">Your Information</h3>
              <p className="text-sm text-theme-muted mb-4">
                We need this to identify you on the leaderboard and send updates. Your email won't be shown publicly.
              </p>

              <div>
                <label htmlFor="name" className="block text-sm font-medium text-theme-primary mb-1">
                  Full Name *
                </label>
                <Field
                  type="text"
                  name="name"
                  className="w-full px-4 py-2 bg-theme-secondary border border-theme-tertiary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-theme-primary"
                  placeholder="John Doe"
                />
                <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
                <p className="text-xs text-theme-muted mt-1">
                  This name will appear on the public leaderboard
                </p>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-theme-primary mb-1">
                  Email Address *
                </label>
                <Field
                  type="email"
                  name="email"
                  className="w-full px-4 py-2 bg-theme-secondary border border-theme-tertiary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-theme-primary"
                  placeholder="your@email.com"
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
                <p className="text-xs text-theme-muted mt-1">
                  We'll send you updates about your performance. Not shown publicly.
                </p>
              </div>

              <div>
                <label htmlFor="socialHandle" className="block text-sm font-medium text-theme-primary mb-1">
                  Social Media Handle (Optional)
                </label>
                <Field
                  type="text"
                  name="socialHandle"
                  className="w-full px-4 py-2 bg-theme-secondary border border-theme-tertiary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-theme-primary"
                  placeholder="@yourhandle or your Twitter/X, YouTube link"
                />
                <ErrorMessage
                  name="socialHandle"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
                <p className="text-xs text-theme-muted mt-1">
                  Share your social media to build your reputation and connect with other investors
                </p>
              </div>

              <div>
                <label htmlFor="experienceLevel" className="block text-sm font-medium text-theme-primary mb-1">
                  Investment Experience (Optional)
                </label>
                <Field
                  as="select"
                  name="experienceLevel"
                  className="w-full px-4 py-2 bg-theme-secondary border border-theme-tertiary rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-theme-primary"
                >
                  <option value="">Select your experience level...</option>
                  <option value="beginner">Beginner (0-2 years)</option>
                  <option value="intermediate">Intermediate (3-5 years)</option>
                  <option value="advanced">Advanced (6-10 years)</option>
                  <option value="professional">Professional (10+ years or Finance Professional)</option>
                </Field>
                <ErrorMessage name="experienceLevel" component="div" className="text-red-500 text-sm mt-1" />
                <p className="text-xs text-theme-muted mt-1">
                  We may create separate leaderboards by experience level in the future
                </p>
              </div>
            </div>

            {/* Stock Picks Section */}
            <div className="bg-theme-tertiary/30 p-6 rounded-lg space-y-4">
              <h3 className="text-xl font-semibold text-theme-primary mb-4 flex items-center gap-2">
                <Search size={24} />
                Your 5 Stock Picks *
              </h3>
              <p className="text-theme-muted text-sm mb-4">
                Choose exactly 5 stocks. No duplicates allowed. Equal-weighted $10,000 portfolio.
              </p>

              {[1, 2, 3, 4, 5].map((num) => {
                const fieldName = `ticker${num}`;
                return (
                  <div key={num}>
                    <label
                      htmlFor={fieldName}
                      className="block text-sm font-medium text-theme-primary mb-1"
                    >
                      Stock #{num} *
                    </label>
                    <AsyncSelect
                      cacheOptions
                      loadOptions={loadTickerOptions}
                      onChange={(option) =>
                        handleTickerSelect(option as TickerOption | null, fieldName, setFieldValue)
                      }
                      value={selectedTickers[fieldName]}
                      placeholder={`Search for stock ${num}...`}
                      styles={customSelectStyles}
                      isClearable
                      components={{
                        DropdownIndicator: () => (
                          <div className="pr-2">
                            <Search size={18} className="text-gray-500" />
                          </div>
                        ),
                      }}
                    />
                    <ErrorMessage
                      name={fieldName}
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                    {selectedTickers[fieldName] && (
                      <div className="mt-2 text-sm text-green-600 flex items-center gap-1">
                        <CheckCircle2 size={16} />
                        Selected: {selectedTickers[fieldName]?.value}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Portfolio Summary */}
            {values.ticker1 && values.ticker2 && values.ticker3 && values.ticker4 && values.ticker5 && (
              <div className="bg-blue-50 dark:bg-blue-900/20 border-2 border-blue-500 p-6 rounded-lg">
                <h4 className="text-lg font-semibold text-blue-800 dark:text-blue-400 mb-3">
                  📊 Your Portfolio Summary
                </h4>
                <div className="space-y-2 text-sm">
                  <p className="text-blue-700 dark:text-blue-300">
                    <strong>Initial Investment:</strong> $10,000 (equal-weighted: $2,000 per stock)
                  </p>
                  <div className="bg-white dark:bg-gray-800 rounded p-3 space-y-1">
                    {[1, 2, 3, 4, 5].map((num) => {
                      const ticker = values[`ticker${num}` as keyof SubmissionFormValues];
                      const price = values[`price${num}` as keyof SubmissionFormValues];
                      const selected = selectedTickers[`ticker${num}`];
                      if (!ticker) return null;
                      return (
                        <div key={num} className="flex justify-between items-center text-theme-primary">
                          <span className="font-medium">
                            {num}. {ticker}
                            {selected?.name && <span className="text-xs ml-1 text-theme-muted">({selected.name})</span>}
                          </span>
                          {price && <span className="text-theme-muted">${price.toFixed(2)}</span>}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-xs text-blue-600 dark:text-blue-400 mt-2">
                    ℹ️ Your picks will be compared daily against the S&P 500 and other participants
                  </p>
                </div>
              </div>
            )}

            {/* Accountability Lock */}
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border-2 border-yellow-500 p-6 rounded-lg">
              <div className="flex items-start gap-3">
                <Lock size={24} className="text-yellow-600 flex-shrink-0 mt-1" />
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-yellow-800 dark:text-yellow-400 mb-2">
                    The Accountability Lock 🔒
                  </h4>
                  <p className="text-yellow-700 dark:text-yellow-300 mb-4 text-sm">
                    Once you submit, these 5 picks are <strong>PERMANENT for the entire year</strong>.
                    No edits, no deletions. Your performance will be tracked daily against the S&P
                    500 and NASDAQ. The world will see if you can beat the market!
                  </p>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Field
                      type="checkbox"
                      name="acceptTerms"
                      className="mt-1 h-5 w-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <span className="text-sm text-yellow-800 dark:text-yellow-300">
                      I understand and accept that my picks will be locked in permanently. I'm ready
                      to be held accountable for my stock predictions.
                    </span>
                  </label>
                  <ErrorMessage
                    name="acceptTerms"
                    component="div"
                    className="text-red-500 text-sm mt-2"
                  />
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              {!showLockInWarning ? (
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 px-6 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isSubmitting ? (
                    'Submitting...'
                  ) : (
                    <>
                      <AlertCircle size={20} />
                      Review Submission
                    </>
                  )}
                </button>
              ) : (
                <div className="space-y-3">
                  <div className="bg-red-50 dark:bg-red-900/20 border-2 border-red-500 p-4 rounded-lg">
                    <p className="text-red-700 dark:text-red-300 font-semibold text-center">
                      ⚠️ FINAL WARNING: This action cannot be undone! ⚠️
                    </p>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full py-4 px-6 bg-red-600 hover:bg-red-700 text-white font-bold rounded-lg transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isSubmitting ? (
                      'Locking In...'
                    ) : (
                      <>
                        <Lock size={20} />
                        LOCK IN MY PICKS (PERMANENT)
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowLockInWarning(false)}
                    className="w-full py-2 px-6 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

